/* eslint-disable react/no-danger */
import React from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';
import { compose } from 'redux';
import cn from 'classnames';
import theme, { sliceColourTheme } from '../styles/theme';
import FullViewportContainer from './FullViewportContainer';
import Link from './Link';
import { linkType, responsiveImageType, colourThemeType } from '../propTypes';
import get from '../helpers/get';
import {
  getSliceContent,
  getSliceAutoplayMilliseconds,
  getSliceItems,
  getSliceColourTheme,
} from '../selectors';
import Swiper from './Scrollers/MaybeSwiper';
import { salesBannerSlideChangedCreator } from '../actions';

const SalesBanner = ({
  autoplay,
  classes,
  className,
  items,
  showArrows,
  onSlideChange,
  padBottom,
  colourTheme,
}) => (
  <FullViewportContainer>
    <div
      className={cn(className, classes.banner, !padBottom && 'noPadding')}
    >
      <Swiper
        loop
        autoplay={autoplay}
        colourTheme={colourTheme}
        classNames={classes}
        slideClassName={classes.link}
        onSlideChange={onSlideChange}
        prevNextButtons={showArrows}
      >
        {items.map(({ clickthrough, icon }, i) => {
          const src = get(icon, ['sizes', 0, 'url']);
          const text = get(clickthrough, ['text', 0, 'text']);
          return (
            <Link
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              className={classes.link}
              link={clickthrough}
            >
              {src && <img alt={icon.alt} className={classes.icon} src={src} />}
              <span className={classes.text} dangerouslySetInnerHTML={{ __html: text }} />
            </Link>
          );
        })}
      </Swiper>
    </div>
  </FullViewportContainer>
);

SalesBanner.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  className: PropTypes.string,
  autoplay: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]).isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      clickthrough: linkType.isRequired,
      icon: responsiveImageType,
    }).isRequired,
  ).isRequired,
  // eslint-disable-next-line react/no-unused-prop-types -- used in mapStateToProps
  sliceId: PropTypes.number.isRequired,
  showArrows: PropTypes.bool,
  onSlideChange: PropTypes.func,
  padBottom: PropTypes.bool,
  colourTheme: colourThemeType,
};

export const defaultColourTheme = sliceColourTheme.grey;

SalesBanner.defaultProps = {
  className: undefined,
  showArrows: false,
  onSlideChange: undefined,
  padBottom: true,
  colourTheme: defaultColourTheme,
};

const mapStateToProps = (state, { sliceId }) => ({
  showArrows: get(getSliceContent(state, sliceId), ['show_arrows']) === 'Yes',
  autoplay: getSliceAutoplayMilliseconds(state, sliceId),
  items: getSliceItems(state, sliceId),
  padBottom: get(getSliceContent(state, sliceId), ['pad_bottom']) !== 'No',
  colourTheme: sliceColourTheme[getSliceColourTheme(state, sliceId)],
});

const mapDispatchToProps = (dispatch, { sliceId: salesBannerId }) => ({
  onSlideChange: slideIndex => dispatch(salesBannerSlideChangedCreator(salesBannerId, slideIndex)),
});

const height = 38;

const buttonStyle = {
  width: 32,
  height: 34,
  background: 'none',
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectSheet({
    banner: {
      backgroundColor: ({ colourTheme: { backgroundColour } }) => backgroundColour,
      color: ({ colourTheme: { textColour } }) => textColour,
      borderLeft: 'none',
      borderRight: 'none',
      fontFamily: theme.fonts.body,
      fontWeight: theme.fonts.bodyWeight,
      fontSize: 16,
      lineHeight: 1.5,
      letterSpacing: 0.3,
      textTransform: 'uppercase',
      textAlign: 'center',
      overflow: 'hidden',
      transition: 'background-color 0.3s, color 0.3s',
      '&.noPadding': {
        marginBottom: '0 !important',
      },
    },
    wrapper: {
      margin: [0, 'auto'],
      maxWidth: theme.breakpoints.width('xl'),
      padding: 0,
      minHeight: height,
      display: 'flex',
      alignItems: 'center',
    },
    content: {
      alignItems: 'center',
    },
    prev: {
      ...buttonStyle,
    },
    next: {
      ...buttonStyle,
    },
    link: {
      alignItems: 'center',
      display: 'flex',
      height: '100%',
      justifyContent: 'center',
      width: `calc(100% - ${theme.padding.horizontal * 2}px)`,
      margin: [8, theme.padding.horizontal],
      '&, .cms-content &[href]': {
        textDecoration: 'none',
      },
      '&, &:visited': {
        color: 'inherit',
      },
      '&:focus, &:hover': {
        color: ({ colourTheme: { linkHover } }) => linkHover,
      },
    },
    text: {
      fontWeight: theme.fonts.semiBold,
      overflow: 'hidden',
    },
    icon: {
      display: 'inline-block',
      width: 25,
      height: 25,
      margin: [-3, 13, -3, 0],
      objectFit: 'contain',
      fontFamily: '"object-fit: contain;"', // object-fit polyfill
    },
  }),
)(SalesBanner);
